import React, { useMemo } from 'react';
import { IBaseDisbursementContainerProps } from '../IBaseDisbursementContainerProps';
import { IACHDisbursementFormData } from 'types/order/escrow/achDisbursements/IACHDisbursementFormData';
import { useField } from 'formik';
import { getISelectOptionsFromIAchAccountResponse } from '../../../../../../ui-toolkit/orders/methods/escrow/getAchAccountOptions';
import { SelectField } from '../../../../../../ui-toolkit/components/SelectField/SelectField';
import { BodyTextTW } from 'tailwindComponents/bodyText/BodyTextTW';
import { TextField } from '../../../../../../ui-toolkit/components/TextField/TextField';
import { NumberField } from '../../../../../../ui-toolkit/components/NumberField/NumberField';
import {
  achDisbursementCategoriesOptions,
  eACHDisbursementCategories,
} from 'types/order/escrow/eEscrowAchPushDisbursementCategories';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { ePartyPurpose } from '../../../../../../ui-toolkit/enums/ePartyPurpose';
import { ISelectOption } from '../../../../../../ui-toolkit/components/Select';
import { accountTypeOptions } from 'types/order/escrow/IAchAccountData';
import { useGetACHAccountsQuery } from 'API/order/escrow/AchDisbursementAPI';
import { AccountNumber } from '../shared/AccountNumber';
import { SendACHButton } from './SendACHButton';
import { HoldbackContainer } from '../holdbacks/HoldbackContainer';
import { VoidACHButton } from '../Voiding/VoidACHButton';
import { FullName, isBuyerBorrower } from '../../../../../../ui-toolkit/orders/interfaces/IParty';
import { DateFormat, getDateString } from '../../../../../../ui-toolkit/utilities/getDateString';
import { RoutingNumberField } from './formFields/RoutingNumberField';
import { GiactField } from './formFields/GiactField';

export const ACHDisbursementContainer: React.FC<IBaseDisbursementContainerProps> = ({
  disbursementType,
  TypeChange,
  isDirty,
}) => {
  const [{ value: data }] = useField<IACHDisbursementFormData>('value');
  const { data: order } = useSelectCurrentOrder();
  assertDefined(order);
  const isReadOnly = disbursementType.isReadOnly(data) || !disbursementType?.hasEditPermission;
  // parties
  const { data: achAccounts = [] } = useGetACHAccountsQuery();
  const borrowerParties = order.parties.filter(isBuyerBorrower);
  const sellerParties = order.parties.filter((party) => {
    return party.purpose === ePartyPurpose.seller;
  });
  const sellerPartyOptions: ISelectOption[] = sellerParties.map((party) => ({
    label: FullName(party),
    value: party.id,
  }));
  const borrowerPartyOptions: ISelectOption[] = borrowerParties.map((party) => ({
    label: FullName(party),
    value: party.id,
  }));

  const achAccountList = useMemo(() => {
    return getISelectOptionsFromIAchAccountResponse(achAccounts);
  }, [achAccounts]);

  return (
    <div className={'my-4 flex basis-full flex-wrap gap-y-2'}>
      <NumberField
        className={'basis-1/3 pr-2'}
        name={`value.amountCents`}
        label="Amount"
        mode="currency"
        placeholder="123,456.78"
        valueIsCents
        readOnly={isReadOnly}
      />
      {TypeChange}
      <HoldbackContainer
        id={data.id}
        isReadOnly={isReadOnly}
        isDirty={isDirty}
        holdbackWarning={<BodyTextTW>Partial Account Information can not be saved</BodyTextTW>}
      />
      <SelectField
        className={'basis-full'}
        name={`value.account`}
        options={achAccountList}
        equalityCheck={(a, b) => a?.id === b?.id}
        label={'Existing ACH Account (Optional)'}
        isClearable
        errorText={''}
        isDisabled={isReadOnly}
      />
      {!data.account?.id && <BodyTextTW>Or enter a new one:</BodyTextTW>}
      {!data.account?.id && (
        <div className={'flex basis-full flex-row flex-wrap gap-y-2'}>
          <TextField
            className={'basis-3/4 pr-2'}
            name={`value.account.name`}
            label="Account Name"
            placeholder="Samuel L. Jackson"
            readOnly={isReadOnly}
          />
          <SelectField
            className={'basis-1/4'}
            name={`value.account.accountType`}
            options={accountTypeOptions}
            label={'Account Type'}
            isDisabled={isReadOnly}
            defaultValue={accountTypeOptions[0]}
          />
          <RoutingNumberField isReadOnly={isReadOnly} />
          <AccountNumber baseName={`value.account`} isDirty={isDirty} isReadOnly={isReadOnly} />
        </div>
      )}
      <TextField
        className={'basis-1/4 pr-2'}
        name={`value.wellsFargoTransactionNumber`}
        label="Memo"
        placeholder="StreetAddress-Zip (ex: 123MainSt-90210)"
        readOnly={isReadOnly}
      />
      <SelectField
        className={'basis-1/4 pr-2'}
        name={`value.category`}
        options={achDisbursementCategoriesOptions}
        label={'Payee Category'}
        isDisabled={isReadOnly}
      />
      {data.category === eACHDisbursementCategories.Borrower && (
        <SelectField
          className={'basis-1/2'}
          name={`value.account.ownerId`}
          options={borrowerPartyOptions}
          label={'Borrower'}
          isDisabled={isReadOnly || !!data.account?.id}
        />
      )}
      {data.category === eACHDisbursementCategories.Seller && (
        <SelectField
          className={'basis-1/2'}
          name={`value.account.ownerId`}
          options={sellerPartyOptions}
          label={'Seller'}
          isDisabled={isReadOnly || !!data.account?.id}
        />
      )}
      <GiactField isReadOnly={isReadOnly} isDirty={isDirty} />
      {data.sentAt && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Initiated Date:</span> {getDateString(DateFormat.short, data.sentAt)}
        </div>
      )}
      {data.disbursedAt && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Disbursement Started Date:</span>{' '}
          {getDateString(DateFormat.short, data.disbursedAt)}
        </div>
      )}
      {data.completedAt && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Disbursed Date:</span> {getDateString(DateFormat.short, data.completedAt)}
        </div>
      )}
      {data.voidedAt && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Voided Date:</span>{' '}
          {getDateString(DateFormat.shortDateTime, data.voidedAt, true)}
        </div>
      )}
      {data.voidReason && (
        <div className={'basis-full'}>
          <span className={'font-semibold'}>Voided Reason:</span> {data.voidReason}
          {data.voidReasonDescription && ` - ${data.voidReasonDescription}`}
        </div>
      )}
      <div className={'flex basis-full justify-end'}>
        {!data.isNew && !isReadOnly && <SendACHButton baseName={'value'} achDisbursement={data} disabled={isDirty} />}
        <VoidACHButton achDisbursement={data} />
      </div>
    </div>
  );
};
